/*------------------------------------*\
    MODULES
\*------------------------------------*/
import debounce from "../helpers/debounce";
import loadGoogleMapsApi from "load-google-maps-api";
import 'whatwg-fetch';
import MicroModal from 'micromodal';

/*------------------------------------*\
    TIMELINE
\*------------------------------------*/
class Map {
    constructor(passedElement) {
        let self = this;

        self.elem = passedElement;
        self.apiKey = self.elem.getAttribute('data-key');
        self.language = self.elem.getAttribute('data-language');
        self.timeline = self.elem.querySelector('[data-map-timeline]');
        self.timelineItemsContainer = self.elem.querySelector('[data-map-timeline-items]');
        self.timelineItems = [];
        self.selectedTimelineItemIndex = null;
        self.timelineScrollButtons = self.elem.querySelector('[data-map-timeline-scroll-buttons]');
        self.timelineLeftButton = self.elem.querySelector('[data-map-timeline-scroll-left]');
        self.timelineRightButton = self.elem.querySelector('[data-map-timeline-scroll-right]');
        self.mapElem = self.elem.querySelector('[data-map]');
        self.activitySummary = self.elem.querySelector('[data-activity-summary]');
        self.activitySummaryButton = self.elem.querySelector('[data-activity-summary-button]');
        self.activitySummaryItems = self.elem.querySelector('[data-activity-summary-items]');
        self.activitySummaryIsOpen = false;
        self.setMarkers = [];
    }

    /*-------------------------------------------------*\
        Init
    \*-------------------------------------------------*/
    init() {
        let self = this;

        // Open the modal
        MicroModal.show('modal');

        // Load the Google Maps API, passing in our API key
        loadGoogleMapsApi({ key: self.apiKey }).then(function (googleMaps) {

            // Call the function that initialises the rendering of the map
            self.googleMaps = googleMaps;

            // Fetch the map data
            self.fetchMapData();

        }).catch(function (error) {
            console.error(error)
        });

        // Show/hide scroll buttons on window resize
        const logDebounce = debounce(function () {
            self.toggleScrollButtons();
        });

        window.addEventListener('resize', function () {
            logDebounce();
        }, false);

        // Handle scroll button clicks
        self.timelineRightButton.addEventListener('click', evt => {
            self.scrollToNextSeason();
        });

        self.timelineLeftButton.addEventListener('click', evt => {
            self.scrollToPreviousSeason();
        });

        // Handle cursor clicks
        document.addEventListener('keydown', evt => {
            if(evt.key === 'ArrowRight') {
                self.scrollToNextSeason();
            }

            if(evt.key === 'ArrowLeft') {
                self.scrollToPreviousSeason();
            }
        });

        // Toggle activity summary dropdown
        self.activitySummaryButton.addEventListener('click', evt => {
            if (self.activitySummaryIsOpen) {
                self.closeActivitySummary();
            } else {
                self.openActivitySummary();
            }
        });

        // Open activity summary on page load?
        if(self.activitySummary.hasAttribute('data-activity-summary-open')) {
            self.openActivitySummary();
        }
    }

    /*-------------------------------------------------*\
        Method for toggling scroll buttons on/off
    \*-------------------------------------------------*/
    toggleScrollButtons() {
        let self = this;

        if (self.timelineItemsContainer.scrollWidth > self.timelineItemsContainer.clientWidth) {
            self.timelineScrollButtons.classList.add('is-active');
        } else {
            self.timelineScrollButtons.classList.remove('is-active');
        }
    }

    scrollToNextSeason() {
        let self = this;

        // Work out the array index of the next timeline item
        let nextTimelineItemIndex = self.selectedTimelineItemIndex + 1;

        // If we're not already on the last season...
        if(nextTimelineItemIndex < self.timelineItems.length) {
            // "Click" the next timeline item
            self.timelineItems[nextTimelineItemIndex].click();
            // Scroll the timeline item to the center of the timeline
            self.timelineItems[nextTimelineItemIndex].scrollIntoView({behavior: "smooth", inline: "center"});
        }
    }

    scrollToPreviousSeason() {
        let self = this;

        // Work out the array index of the previous timeline item
        let previousTimelineItemIndex = self.selectedTimelineItemIndex - 1;

        // If we're not already on the first season...
        if(previousTimelineItemIndex >= 0) {
            // "Click" the previous timeline item
            self.timelineItems[previousTimelineItemIndex].click();
            // Scroll the timeline item to the center of the timeline
            self.timelineItems[previousTimelineItemIndex].scrollIntoView({behavior: "smooth", inline: "center"});
        }
    }

    /*-------------------------------------------------*\
        Open activity summary
    \*-------------------------------------------------*/
    openActivitySummary() {
        let self = this;

        self.activitySummary.classList.add('is-open');
        self.activitySummaryButton.setAttribute('aria-expanded', true);
        self.activitySummaryIsOpen = true;
    }

    /*-------------------------------------------------*\
        Close activity summary
    \*-------------------------------------------------*/
    closeActivitySummary() {
        let self = this;

        self.activitySummary.classList.remove('is-open');
        self.activitySummaryButton.setAttribute('aria-expanded', false);
        self.activitySummaryIsOpen = false;
    }

    /*-------------------------------------------------*\
        Fetch map data
    \*-------------------------------------------------*/
    fetchMapData() {
        let self = this;

        // Fetch the marker data
        fetch(`../data/map-data-${self.language}.json`)
            .then(function (response) {
                return response.json();
            }).then(function (json) {
                self.mapData = json;

                // Render the timeline
                self.renderTimeline();

                // Render the map
                self.renderMap();

                // Listen for click events
                self.events();
            }).catch(function (ex) {
                console.log('Parsing failed', ex);
            });
    }

    /*-------------------------------------------------*\
        Render timeline
    \*-------------------------------------------------*/
    renderTimeline() {
        let self = this;

        self.mapData.map(item => {
            // Create li element
            let li = document.createElement('li');
            li.setAttribute('class', 'timeline__item');

            // Create a element
            let a = document.createElement('a');
            a.setAttribute('class', 'timeline__item-link');
            a.setAttribute('data-map-timeline-item', '');
            a.setAttribute('data-season', item.seasonHandle);
            a.setAttribute('href', '');
            a.textContent = item.seasonName;

            // Append a element to li element
            li.append(a);

            // Append li element to self.timelineItemsContainer
            self.timelineItemsContainer.append(li);

            // Show scroll buttons (if necessary)
            self.toggleScrollButtons();

            // Add timeline item to global self.timelineItems var
            self.timelineItems.push(a);
        });
    }

    /*-------------------------------------------------*\
        Render map
    \*-------------------------------------------------*/
    renderMap() {
        let self = this;

        // Create the map options
        let mapOptions = {
            zoom: 14,
            maxZoom: 16,
            center: new self.googleMaps.LatLng(53.1548855, -4.1417933),
            disableDefaultUI: true,
            zoomControl: true,
            styles: [{ "featureType": "water", "elementType": "geometry", "stylers": [{ "color": "#e9e9e9" }, { "lightness": 17 }] }, { "featureType": "landscape", "elementType": "geometry", "stylers": [{ "color": "#f5f5f5" }, { "lightness": 20 }] }, { "featureType": "road.highway", "elementType": "geometry.fill", "stylers": [{ "color": "#ffffff" }, { "lightness": 17 }] }, { "featureType": "road.highway", "elementType": "geometry.stroke", "stylers": [{ "color": "#ffffff" }, { "lightness": 29 }, { "weight": 0.2 }] }, { "featureType": "road.arterial", "elementType": "geometry", "stylers": [{ "color": "#ffffff" }, { "lightness": 18 }] }, { "featureType": "road.local", "elementType": "geometry", "stylers": [{ "color": "#ffffff" }, { "lightness": 16 }] }, { "featureType": "poi", "elementType": "geometry", "stylers": [{ "color": "#f5f5f5" }, { "lightness": 21 }] }, { "featureType": "poi.park", "elementType": "geometry", "stylers": [{ "color": "#dedede" }, { "lightness": 21 }] }, { "elementType": "labels.text.stroke", "stylers": [{ "visibility": "on" }, { "color": "#ffffff" }, { "lightness": 16 }] }, { "elementType": "labels.text.fill", "stylers": [{ "saturation": 36 }, { "color": "#333333" }, { "lightness": 40 }] }, { "elementType": "labels.icon", "stylers": [{ "visibility": "off" }] }, { "featureType": "transit", "elementType": "geometry", "stylers": [{ "color": "#f2f2f2" }, { "lightness": 19 }] }, { "featureType": "administrative", "elementType": "geometry.fill", "stylers": [{ "color": "#fefefe" }, { "lightness": 20 }] }, { "featureType": "administrative", "elementType": "geometry.stroke", "stylers": [{ "color": "#fefefe" }, { "lightness": 17 }, { "weight": 1.2 }] }]
        };

        // Create the map
        self.map = new self.googleMaps.Map(self.mapElem, mapOptions);

        // Render overlays
        self.renderOverlay();

        // Load the first season
        self.goToSeason(self.mapData[0], self.timelineItems[0]);
    }

    /*-------------------------------------------------*\
        Render overlay
    \*-------------------------------------------------*/
    renderOverlay() {
        let self = this;

        // Set overlay bounds
        const overlayBounds = {
            north: 53.18907, // increase = up
            south: 53.11765, // increase = up
            east: -4.09716, // increase = left
            west: -4.17313, // increase = left
        };

        // Create overlay
        const overlay = new google.maps.GroundOverlay(
            "../assets/images/map-overlay.svg",
            overlayBounds
        );

        // Add overlay to map
        overlay.setMap(self.map);
    }

    /*-------------------------------------------------*\
        Render markers
    \*-------------------------------------------------*/
    renderMarkers(season) {
        let self = this;

        // Create empty array to store the markers
        let markers = [];

        // Get the markers from the season data
        season.activities.map(activity => {
            activity.markers.map(marker => {
                marker.number = activity.number;
                marker.seasonName = season.seasonName;
                marker.color = activity.color;
                markers.push(marker);
            });
        });

        // Create a new infoWindow
        let infoWindow = new self.googleMaps.InfoWindow();

        // Create the marker icon
        let icon = {
            path: 'M40.8,20.4A20.4,20.4,0,0,0,0,20.4C0,31.7,20.4,52.8,20.4,52.8S40.8,31.7,40.8,20.4',
            fillOpacity: 1,
            strokeColor: '#ffffff',
            strokeWeight: 1,
            scale: 1,
            labelOrigin: new google.maps.Point(20, 22),
            origin: new google.maps.Point(0, 0),
            anchor: new google.maps.Point(20, 53)

        };

        // Create the bounds object, which will be configured later based on the marker locations
        let bounds = new self.googleMaps.LatLngBounds();

        // Loop through the markers
        for (let i = 0; i < markers.length; i++) {
            // Get the marker data
            let markerData = markers[i];

            // Create a new LatLng object to use for the marker position
            let latLng = new self.googleMaps.LatLng(markerData.lat, markerData.lng);

            icon.fillColor = markerData.color;

            // Create the marker object
            let marker = new self.googleMaps.Marker({
                position: latLng,
                map: self.map,
                icon: icon,
                label: {
                    color: '#fff', fontSize: '18px', fontWeight: '600',
                    text: `${markerData.number}`
                },
                title: markerData.description
            });

            // Add this marker to the self.setMarkers global array
            self.setMarkers.push(marker);

            // Handle marker clicks
            self.googleMaps.event.addListener(marker, "click", function (e) {

                // Build the content for the infoWindow
                let infoWindowContent = `
                    <div class="map__info-window">
                        <h2 class="map__info-window-heading">${markerData.seasonName}</h2>
                        ${markerData.description}
                    </div>
                `;

                // Add the content to the infoWindow
                infoWindow.setContent(infoWindowContent);
                infoWindow.open(self.map, marker);
            });

            // Work out the area of the map containing markers
            bounds.extend(marker.position);

            // Create global function to center the map with all markers visible
            self.fitAndCenter = function () {
                self.map.setCenter(bounds.getCenter());
                self.map.fitBounds(bounds);
            };

            // Fit and center
            self.fitAndCenter();
        }
    }

    /*-------------------------------------------------*\
        Clear markers
    \*-------------------------------------------------*/
    clearMarkers() {
        let self = this;

        for (let i = 0; i < self.setMarkers.length; i++) {
            self.setMarkers[i].setMap(null);
        }

        self.setMarkers = [];
    }

    /*-------------------------------------------------*\
        Switch to the specified season
    \*-------------------------------------------------*/
    goToSeason(season, timelineItem) {
        let self = this;

        // Remove active class from all timeline items
        self.timelineItems.map(item => {
            item.classList.remove('is-active');
        });

        // Add active class to clicked button
        timelineItem.classList.add('is-active');

        // Render the new set of markers
        self.renderMarkers(season);

        self.renderActivitySummaryItems(season);

        self.selectedTimelineItemIndex = self.timelineItems.findIndex((arrayItem) => arrayItem == timelineItem);
    }

    /*-------------------------------------------------*\
        Render activity summary items
    \*-------------------------------------------------*/
    renderActivitySummaryItems(season) {
        let self = this;

        self.activitySummaryItems.innerHTML = '';

        // Add activity summary items to the activity summary dropdown
        season.activities.map(item => {
            self.activitySummaryItems.insertAdjacentHTML('beforeend', `
                <li class="activity-summary__item">
                    <span class="activity-summary__item-icon" style="color:${item.color}">
                        <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 41 53'><path d='M40.8,20.4A20.4,20.4,0,0,0,0,20.4C0,31.7,20.4,52.8,20.4,52.8S40.8,31.7,40.8,20.4' style='fill:currentColor;fill-rule:evenodd'/></svg>
                        <span class="activity-summary__item-icon-number">${item.number}</span>
                    </span>
                    <button class="[ activity-summary__item-label ] [ stretched-link ]" type="button" data-activity-summary-item-btn data-center-point-lat="${item.centerPoint.lat}" data-center-point-lng="${item.centerPoint.lng}">${item.summary}</button>
                </li>
            `);
        });

        // Get all the activity summary item buttons
        let activitySummaryItemButtons = self.activitySummary.querySelectorAll('[data-activity-summary-item-btn]');

        // Loop through the buttons and listen for clicks
        [...activitySummaryItemButtons].map(item => {
            item.addEventListener('click', evt => {
                // Pan to the center point of the markers
                self.map.panTo(new self.googleMaps.LatLng(
                    item.getAttribute('data-center-point-lat'),
                    item.getAttribute('data-center-point-lng')
                ));

                // Get the current zoom level
                let currentZoom = self.map.getZoom();
                console.log(currentZoom);
                
                if (currentZoom < 15) {
                    self.map.setZoom(15);
                }
            });
        });
    }

    /*-------------------------------------------------*\
        Events
    \*-------------------------------------------------*/
    events() {
        let self = this;

        // Loop through timeline items
        self.timelineItems.map(item => {

            // Handle timeline item clicks
            item.addEventListener('click', evt => {
                evt.preventDefault();

                // Clear all markers from the map
                self.clearMarkers();

                // Get the handle of the season to switch to
                let seasonHandle = item.getAttribute('data-season');

                // Find the season from the mapData
                let newSeason = self.mapData.find(season => season.seasonHandle == seasonHandle);

                // Scroll to align the timeline to the top of the viewport
                self.timeline.scrollIntoView();

                // Load the season
                self.goToSeason(newSeason, item);
            });
        });

        // Re-center and fit the map when the window is resized
        self.googleMaps.event.addDomListener(window, 'resize', function () {
            self.fitAndCenter();
        });
    }
}

export default Map;
